import React, { useState, useEffect } from 'react';
import { useComunicacao } from '../../contexts/ComunicacaoContext';
import { useUsuario } from '../../contexts/UsuarioContext';
import { toast } from 'react-toastify';

interface ModalCriarCanalProps {
  onClose: () => void;
}

const ModalCriarCanal: React.FC<ModalCriarCanalProps> = ({ onClose }) => {
  const { criarCanal } = useComunicacao();
  const { usuarios, carregarUsuarios } = useUsuario();
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [membrosSelecionados, setMembrosSelecionados] = useState<string[]>([]);
  const [busca, setBusca] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    carregarUsuarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const usuariosFiltrados = usuarios.filter(u =>
    (u.nome?.toLowerCase() || '').includes(busca.toLowerCase()) ||
    (u.email?.toLowerCase() || '').includes(busca.toLowerCase())
  );

  const toggleMembro = (usuarioId: string) => {
    setMembrosSelecionados(prev =>
      prev.includes(usuarioId)
        ? prev.filter(id => id !== usuarioId)
        : [...prev, usuarioId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!nome.trim()) {
      return;
    }

    if (membrosSelecionados.length === 0) {
      toast.error('Selecione pelo menos um membro para o canal');
      return;
    }

    try {
      setSalvando(true);
      await criarCanal({
        nome: nome.trim(),
        descricao: descricao.trim(),
        membros: membrosSelecionados
      });
      onClose();
    } catch (error) {
      console.error('Erro ao criar canal:', error);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800">Criar Canal Personalizado</h2>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome do Canal *
            </label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Gestores, Equipe de Vendas..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-bravo-primary focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descrição
            </label>
            <textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Descreva o propósito deste canal..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-bravo-primary focus:border-transparent resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Membros * ({membrosSelecionados.length} selecionados)
            </label>
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar usuários..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-bravo-primary focus:border-transparent mb-3"
            />

            <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto">
              {usuariosFiltrados.map((usuario) => (
                <label
                  key={usuario.id}
                  className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <input
                    type="checkbox"
                    checked={membrosSelecionados.includes(usuario.id)}
                    onChange={() => toggleMembro(usuario.id)}
                    className="mr-3 h-4 w-4 text-bravo-primary focus:ring-bravo-primary border-gray-300 rounded"
                  />
                  <img
                    src={usuario.avatar || '/profilepicture.png'}
                    alt={usuario.nome}
                    className="w-8 h-8 rounded-full mr-3"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">{usuario.nome}</div>
                    <div className="text-sm text-gray-500">{usuario.email}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </form>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            disabled={salvando}
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!nome.trim() || membrosSelecionados.length === 0 || salvando}
            className="px-4 py-2 bg-bravo-primary hover:bg-blue-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {salvando ? 'Criando...' : 'Criar Canal'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalCriarCanal;
